/**
 * BikeDescentProfileChart
 *
 * Elevation profile with per-descent average speed for both riders.
 * Speed lines drawn only within descent segments; null elsewhere.
 *
 * X-axis  : course distance (km)
 * Left Y  : avg speed km/h (within descents only)
 * Right Y : elevation (m)
 */

import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

const CATEGORY_FILL = {
  'B-D4':  'rgba(147,197,253,0.18)',
  'B-D3':  'rgba(96,165,250,0.20)',
  'B-D2':  'rgba(139,92,246,0.22)',
  'B-D1':  'rgba(124,58,237,0.26)',
  'B-DHC': 'rgba(91,33,182,0.30)',
};

const CATEGORY_STROKE = {
  'B-D4':  '#93c5fd',
  'B-D3':  '#60a5fa',
  'B-D2':  '#8b5cf6',
  'B-D1':  '#7c3aed',
  'B-DHC': '#5b21b6',
};

function fmtSpd(v) { return v == null ? '—' : `${v.toFixed(1)} km/h`; }

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const km = (label / 1000).toFixed(2);
  const by = Object.fromEntries(payload.map((p) => [p.dataKey, p.value]));
  return (
    <Box sx={{ backgroundColor: 'rgba(15,23,42,0.92)', border: '1px solid rgba(100,116,139,0.4)', borderRadius: 1, p: '8px 12px', fontSize: '0.75rem', lineHeight: 1.8 }}>
      <Typography variant="caption" color="#94a3b8" display="block">km {km}</Typography>
      {by.spd_a != null
        ? <Typography variant="caption" display="block" sx={{ color: COLOR_A }}>A avg speed: {fmtSpd(by.spd_a)}</Typography>
        : <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>A speed: N/A</Typography>}
      {by.spd_b != null
        ? <Typography variant="caption" display="block" sx={{ color: COLOR_B }}>B avg speed: {fmtSpd(by.spd_b)}</Typography>
        : <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>B speed: N/A</Typography>}
      {by.altitude != null && (
        <Typography variant="caption" display="block" sx={{ color: '#94a3b8' }}>Elevation: {Math.round(by.altitude)} m</Typography>
      )}
    </Box>
  );
}

export default function BikeDescentProfileChart({
  resampled = [],
  commonDescents = null,
  descentsA = [],
  selectedDescent = null,
  onDescentClick = null,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const descents = useMemo(() => {
    if (commonDescents && commonDescents.length > 0) return commonDescents;
    return descentsA || [];
  }, [commonDescents, descentsA]);

  const sortedDescents = useMemo(() => [...descents].sort((a, b) => a.start_distance_m - b.start_distance_m), [descents]);

  const chartData = useMemo(() => {
    if (!resampled.length) return [];
    const step = Math.max(1, Math.floor(resampled.length / 600));
    return resampled.filter((_, i) => i % step === 0).map((r) => {
      const dist = r.course_distance_m;
      const inDescent = sortedDescents.find((d) => dist >= d.start_distance_m && dist <= d.end_distance_m);
      return {
        dist,
        altitude: r.altitude_a ?? null,
        spd_a: inDescent ? (inDescent.a?.avg_speed ?? null) : null,
        spd_b: inDescent ? (inDescent.b?.avg_speed ?? null) : null,
      };
    });
  }, [resampled, sortedDescents]);

  const spdDomain = useMemo(() => {
    const vals = descents.flatMap((d) => [d.a?.avg_speed, d.b?.avg_speed]).filter((v) => v != null);
    if (!vals.length) return [0, 80];
    return [0, Math.ceil(Math.max(...vals)) + 5];
  }, [descents]);

  const elevDomain = useMemo(() => {
    const vals = chartData.map((r) => r.altitude).filter((v) => v != null);
    if (!vals.length) return [0, 3000];
    return [Math.floor(Math.min(...vals) / 50) * 50, Math.ceil(Math.max(...vals) / 50) * 50];
  }, [chartData]);

  const naZones = useMemo(() => {
    if (!chartData.length) return [];
    const xMax = chartData[chartData.length - 1]?.dist ?? 0;
    const zones = [];
    let pos = 0;
    for (const d of sortedDescents) {
      if (d.start_distance_m - pos > 500) zones.push({ x1: pos, x2: d.start_distance_m });
      pos = d.end_distance_m;
    }
    if (xMax - pos > 500) zones.push({ x1: pos, x2: xMax });
    return zones;
  }, [sortedDescents, chartData]);

  const handleChartClick = (data) => {
    if (!onDescentClick || data?.activeLabel == null) return;
    const hit = sortedDescents.find((d) => data.activeLabel >= d.start_distance_m && data.activeLabel <= d.end_distance_m);
    if (hit) onDescentClick(hit);
  };

  if (!chartData.length) return null;

  const xMax = chartData[chartData.length - 1]?.dist ?? 0;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
        Elevation &amp; Speed Profile
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} display="block" mb={1.5}>
        Speed lines show each rider's average for that descent only.
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart
          data={chartData}
          margin={isMobile ? { top: 55, right: 5, left: 0, bottom: 0 } : { top: 55, right: 60, left: 0, bottom: 0 }}
          onClick={handleChartClick}
          style={{ cursor: onDescentClick ? 'pointer' : 'default' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} vertical={false} />
          <XAxis dataKey="dist" type="number" domain={[0, xMax]}
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}`}
            stroke={colors.grey[500]} tick={{ fill: colors.grey[400], fontSize: isMobile ? 9 : 11 }}
          />
          <YAxis yAxisId="spd" orientation="left" domain={spdDomain}
            tickFormatter={(v) => `${v}`}
            stroke={colors.grey[500]} tick={{ fill: colors.grey[400], fontSize: 10 }}
            width={isMobile ? 28 : 42}
            label={isMobile ? undefined : { value: 'km/h', angle: -90, position: 'insideLeft', offset: 10, fill: colors.grey[400], fontSize: 10 }}
          />
          <YAxis yAxisId="elev" orientation="right" domain={elevDomain}
            tickFormatter={(v) => `${Math.round(v)}`}
            stroke={colors.grey[500]} tick={isMobile ? false : { fill: colors.grey[400], fontSize: 10 }}
            width={isMobile ? 0 : 45} axisLine={!isMobile} tickLine={!isMobile}
            label={isMobile ? undefined : { value: 'm', angle: 90, position: 'insideRight', offset: 10, fill: colors.grey[400], fontSize: 10 }}
          />

          {sortedDescents.map((d, i) => {
            const isSel = selectedDescent != null && Math.abs(d.start_distance_m - selectedDescent.start_distance_m) < 1;
            return (
              <ReferenceArea key={`band-${i}`} yAxisId="elev" x1={d.start_distance_m} x2={d.end_distance_m}
                fill={isSel ? (CATEGORY_STROKE[d.category] || '#94a3b8') + '55' : CATEGORY_FILL[d.category] || 'rgba(148,163,184,0.15)'}
                stroke={CATEGORY_STROKE[d.category] || '#94a3b8'}
                strokeWidth={isSel ? 2.5 : 1} strokeOpacity={isSel ? 1 : 0.7} ifOverflow="visible"
              />
            );
          })}

          {sortedDescents.map((d, i) => (
            <ReferenceLine key={`cat-${i}`} yAxisId="elev" x={d.start_distance_m} stroke="none"
              label={{ value: d.category || '', position: 'insideTopRight', fill: CATEGORY_STROKE[d.category] || '#94a3b8', fontSize: 9, fontWeight: 'bold' }}
            />
          ))}

          {naZones.map((z, i) => (
            <ReferenceArea key={`na-${i}`} yAxisId="spd" x1={z.x1} x2={z.x2} fill="transparent" stroke="none"
              label={{ value: 'N/A', position: 'center', fill: colors.grey[600], fontSize: 11, fontStyle: 'italic' }}
            />
          ))}

          <Area yAxisId="elev" type="monotone" dataKey="altitude"
            stroke={colors.grey[500]} strokeWidth={1} fill={colors.grey[800]} fillOpacity={0.6}
            dot={false} activeDot={false} name="Elevation" legendType="none"
          />
          <Line yAxisId="spd" type="stepAfter" dataKey="spd_a"
            stroke={COLOR_A} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }}
            name="A avg speed" connectNulls={false}
          />
          <Line yAxisId="spd" type="stepAfter" dataKey="spd_b"
            stroke={COLOR_B} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }}
            name="B avg speed" connectNulls={false}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} formatter={(value) => <span style={{ color: colors.grey[200] }}>{value}</span>} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
